import type { Enduser, FormField } from "@tellescope/types-models";
import type { TellescopeFieldResponse } from "../types";

/** Identity values a Redirect field's target URL may template in */
const TEMPLATE_KEYS = ["id", "email", "fname", "lname", "state", "phone"] as const;

type TemplateKey = (typeof TEMPLATE_KEYS)[number];

export interface ResolveRedirectUrlArgs {
  externalUrl: string;
  enduser: Partial<Enduser> & { id?: string };
  responses: TellescopeFieldResponse[];
  fieldsMap: Record<string, Pick<FormField, "intakeField">>;
  currentParams?: URLSearchParams;
}

const findAnsweredIntakeValue = (
  key: TemplateKey,
  responses: TellescopeFieldResponse[],
  fieldsMap: Record<string, Pick<FormField, "intakeField">>
): string | undefined => {
  const answered = responses.filter((r) => {
    const intakeField = fieldsMap[r.fieldId]?.intakeField;
    if (intakeField !== key) return false;
    return typeof r.answer?.value === "string" && r.answer.value !== "";
  });
  const latest = answered[answered.length - 1]?.answer?.value;
  return typeof latest === "string" ? latest : undefined;
};

const CREDENTIAL_PARAM_PATTERN =
  /^(a|token|.*authtoken|accesscode|access_token|.*sessiontoken|resettoken|reset_token|apikey|api_key|password|secret)$/i;

/**
 * Normalizes a configured redirect target to something safe to hand to `window.location`.
 *
 * A scheme-less value gains `https://`. Without this the browser resolves it relative to the
 * current document, so `example.com/done` configured on the `/link/form` deeplink navigates to
 * `<portal-url>/link/example.com/done` instead of leaving the portal.
 *
 * Returns `undefined` for a scheme that is neither http nor https, so callers can decline to
 * navigate rather than following a `javascript:` or `data:` target.
 */
export const toAbsoluteUrl = (url: string): string | undefined => {
  if (url.startsWith("//")) return url;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) return `https://${url}`;
  return /^https?:/i.test(url) ? url : undefined;
};

export const resolveRedirectUrl = ({
  externalUrl,
  enduser,
  responses,
  fieldsMap,
  currentParams,
}: ResolveRedirectUrlArgs): string | undefined => {
  const substituted = TEMPLATE_KEYS.reduce((url, key) => {
    const token = `{{${key}}}`;
    if (!url.includes(token)) return url;

    const fromAnswers = findAnsweredIntakeValue(key, responses, fieldsMap);
    const fromEnduser = enduser[key];
    const value = fromAnswers ?? (typeof fromEnduser === "string" ? fromEnduser : "");
    return url.split(token).join(encodeURIComponent(value));
  }, externalUrl);

  const absolute = toAbsoluteUrl(substituted);
  if (!absolute) return undefined;

  if (
    substituted === externalUrl ||
    !currentParams ||
    Array.from(currentParams).length === 0 ||
    absolute.startsWith("//")
  ) {
    return absolute;
  }

  const target = new URL(absolute);
  currentParams.forEach((value, key) => {
    if (CREDENTIAL_PARAM_PATTERN.test(key)) return;
    if (!target.searchParams.has(key)) target.searchParams.set(key, value);
  });
  return target.toString();
};
